"use client";
import {
  Box,
  Center,
  Separator,
  Flex,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  Table,
} from "@chakra-ui/react";
import { Heading } from "@/elements";
import PricingFooter from "@/components/pricingFooter";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";

type ServerTableData = {
  sku: string;
  cpu: string;
  memory: string;
  user_disk: string;
  pricing: {
    linux: string;
    windows: string;
  };
};

type ServerTableProps = {
  heading: string;
  showHeader?: boolean;
  data: ServerTableData[];
  footer?: boolean;
};

const FOOTER_CONSTANTS = {
  btnText: "Test Drive our VDI Solution",
};

export const DesktopView = ({ data }: { data: ServerTableData[] }) => {
  return (
    <Box>
      <Table.Root striped border="2px solid rgb(237,242,247)" width={"100%"}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader rowSpan={2} textAlign={"center"}>
              SKU
            </Table.ColumnHeader>
            <Table.ColumnHeader rowSpan={2} textAlign={"center"} textTransform={"none"}>
              vCPU
            </Table.ColumnHeader>
            <Table.ColumnHeader rowSpan={2} textAlign={"center"}>
              Memory
            </Table.ColumnHeader>
            <Table.ColumnHeader rowSpan={2} textAlign={"center"}>
              User Disk
            </Table.ColumnHeader>
            <Table.ColumnHeader
              colSpan={2}
              textAlign={"center"}
              border="2px solid #EDF2F7"
            >
              Pricing / month
            </Table.ColumnHeader>
          </Table.Row>
          <Table.Row>
            <Table.ColumnHeader textAlign={"center"} border="2px solid #EDF2F7">
              Linux
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign={"center"} border="2px solid #EDF2F7">
              Windows
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {data.map((server, index) => (
            <Table.Row key={index}>
              <Table.Cell textAlign={"center"}>{server.sku}</Table.Cell>
              <Table.Cell textAlign={"center"}>{server.cpu}</Table.Cell>
              <Table.Cell textAlign={"center"}>{server.memory}</Table.Cell>
              <Table.Cell textAlign={"center"}>{server.user_disk}</Table.Cell>
              <Table.Cell
                textAlign={"center"}
                fontWeight={"700"}
                fontSize={"18px"}
                color="primary.200"
              >
                {server.pricing.linux}
              </Table.Cell>
              <Table.Cell
                textAlign={"center"}
                fontWeight={"700"}
                fontSize={"18px"}
                color="primary.200"
              >
                {server.pricing.windows}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const MobileView = ({ data }: { data: ServerTableData[] }) => {
  return (
    <SimpleGrid columns={2} gap={6}>
      {data.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          gap={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {plan.sku}
            </Text>
          </Flex>

          <Separator borderColor="gray.200" />

          <Stack gap={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                CPU : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.cpu}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                User Disk : {""}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.user_disk}
              </Text>
            </Box>
          </Stack>

          <Separator borderColor="gray.200" />

          <Stack gap={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Linux Pricing : {""}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
                {plan.pricing.linux} /mo
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Windows Pricing : {""}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
                {plan.pricing.windows} /mo
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const OrganizationServerTable = (props: ServerTableProps) => {
  const { showHeader, heading, data, footer } = props;
  const HeadingVariant = useHeadingVariant();
  return (
    <Box id="dedicated-server-for-organization">
      {showHeader && (
        <Heading variant={HeadingVariant} textAlign={"center"} mb={8}>
          {heading}
        </Heading>
      )}
      <Center display={{ md: "none" }} w="full">
        <MobileView data={data} />
      </Center>
      <Box display={{ base: "none", md: "block" }}>
        <DesktopView data={data} />
      </Box>
      {footer && <PricingFooter {...FOOTER_CONSTANTS} />}
      <Separator borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default OrganizationServerTable;
